/**
 * Date utilities for consistent date range handling across the application
 * Uses dayjs for immutable, reliable date operations
 */
import dayjs from 'dayjs'

/**
 * Preset date range types
 */
export const DATE_RANGE_PRESETS = {
  LAST_7_DAYS: 'last7Days',
  LAST_30_DAYS: 'last30Days',
  LAST_90_DAYS: 'last90Days',
  LAST_365_DAYS: 'last365Days',
  LAST_WEEK: 'lastWeek',
  LAST_MONTH: 'lastMonth',
  LAST_YEAR: 'lastYear',
  YEAR_TO_DATE: 'yearToDate',
}

/**
 * Get preset date range configurations
 */
export const PRESET_OPTIONS = [
  [
    { label: 'Last 7 Days', value: DATE_RANGE_PRESETS.LAST_7_DAYS },
    { label: 'Last 30 Days', value: DATE_RANGE_PRESETS.LAST_30_DAYS },
    { label: 'Last 90 Days', value: DATE_RANGE_PRESETS.LAST_90_DAYS },
    { label: 'Last 365 Days', value: DATE_RANGE_PRESETS.LAST_365_DAYS },
  ],
  [
    { label: 'Last Week', value: DATE_RANGE_PRESETS.LAST_WEEK },
    { label: 'Last Month', value: DATE_RANGE_PRESETS.LAST_MONTH },
    { label: 'Last Year', value: DATE_RANGE_PRESETS.LAST_YEAR },
  ],
  [{ label: 'Year to Date', value: DATE_RANGE_PRESETS.YEAR_TO_DATE }],
]

/**
 * Create a date range object for q-date component (string format)
 * @param {dayjs.Dayjs} startDate - Start date as dayjs object
 * @param {dayjs.Dayjs} endDate - End date as dayjs object
 * @returns {Object} Date range object with from/to string dates
 */
export function createDateRangeObject(startDate, endDate) {
  return {
    from: startDate.format('YYYY-MM-DD'),
    to: endDate.format('YYYY-MM-DD'),
  }
}

/**
 * Create a date range string for API calls (timestamp format)
 * @param {dayjs.Dayjs} startDate - Start date as dayjs object
 * @param {dayjs.Dayjs} endDate - End date as dayjs object
 * @returns {string} Date range in "startTimestamp|endTimestamp" format
 */
export function createDateRangeString(startDate, endDate) {
  return `${startDate.valueOf()}|${endDate.valueOf()}`
}

/**
 * Convert date range object to API format string
 * @param {Object} dateRangeObject - Object with from/to string dates
 * @returns {string} Date range in "startTimestamp|endTimestamp" format
 */
export function dateRangeObjectToApiFormat(dateRangeObject) {
  if (!dateRangeObject) {
    return null
  }

  // Parse dates and set to start/end of day
  const startDate = dayjs(dateRangeObject.from || dateRangeObject).startOf('day')
  const endDate = dayjs(dateRangeObject.to || dateRangeObject).endOf('day')

  return createDateRangeString(startDate, endDate)
}

function getDayStartEndByRangeType(rangeType) {
  const today = dayjs()
  let startDate
  let endDate = today

  switch (rangeType) {
    case DATE_RANGE_PRESETS.LAST_7_DAYS:
      startDate = today.subtract(7, 'day')
      break
    case DATE_RANGE_PRESETS.LAST_30_DAYS:
      startDate = today.subtract(30, 'day')
      break
    case DATE_RANGE_PRESETS.LAST_90_DAYS:
      startDate = today.subtract(90, 'day')
      break
    case DATE_RANGE_PRESETS.LAST_365_DAYS:
      startDate = today.subtract(365, 'day')
      break
    case DATE_RANGE_PRESETS.LAST_WEEK:
      // Previous complete week (Sunday to Saturday)
      startDate = today.subtract(1, 'week').startOf('week')
      endDate = today.subtract(1, 'week').endOf('week')
      break
    case DATE_RANGE_PRESETS.LAST_MONTH:
      // Previous complete month
      startDate = today.subtract(1, 'month').startOf('month')
      endDate = today.subtract(1, 'month').endOf('month')
      break
    case DATE_RANGE_PRESETS.LAST_YEAR:
      // Previous complete year
      startDate = today.subtract(1, 'year').startOf('year')
      endDate = today.subtract(1, 'year').endOf('year')
      break
    case DATE_RANGE_PRESETS.YEAR_TO_DATE:
      startDate = today.startOf('year')
      break
    default:
      return null
  }

  // Set to start of day for startDate and end of day for endDate
  const startOfDay = startDate.startOf('day')
  const endOfDay = endDate.endOf('day')

  return { startOfDay, endOfDay }
}

/**
 * Get preset date range as object (for q-date component)
 * @param {string} rangeType - One of DATE_RANGE_PRESETS values
 * @returns {Object|null} Date range object or null if invalid type
 */
export function getPresetDateRangeObject(rangeType) {
  const { startOfDay, endOfDay } = getDayStartEndByRangeType(rangeType)
  return createDateRangeObject(startOfDay, endOfDay)
}

/**
 * Get preset date range as API format string
 * @param {string} rangeType - One of DATE_RANGE_PRESETS values
 * @returns {string|null} Date range string or null if invalid type
 */
export function getPresetDateRangeString(rangeType) {
  const { startOfDay, endOfDay } = getDayStartEndByRangeType(rangeType)
  return createDateRangeString(startOfDay, endOfDay)
}

/**
 * Get default date range (last 30 days) as object
 * @returns {Object} Date range object
 */
export function getDefaultDateRangeObject() {
  return getPresetDateRangeObject(DATE_RANGE_PRESETS.LAST_30_DAYS)
}

/**
 * Get default date range (last 30 days) as API format string
 * @returns {string} Date range string
 */
export function getDefaultDateRangeString() {
  return getPresetDateRangeString(DATE_RANGE_PRESETS.LAST_30_DAYS)
}

/**
 * Format date range for display
 * @param {Object} dateRangeObject - Object with from/to string dates
 * @returns {string} Formatted date range string
 */
export function formatDateRangeForDisplay(dateRangeObject) {
  // if (!dateRangeObject || !dateRangeObject.from || !dateRangeObject.to) {
  //   return 'Select Date Range'
  // }

  // single date selected
  if (!dateRangeObject.to || dateRangeObject.from === dateRangeObject.to) {
    const singleDate = dayjs(dateRangeObject.from || dateRangeObject)
    return singleDate.format('MMM D, YYYY')
  }

  const fromDate = dayjs(dateRangeObject.from)
  const toDate = dayjs(dateRangeObject.to)

  // Format as "Jan 1, 2024 - Jan 31, 2024"
  // If same year and month, show "Jan 1 - 31, 2024" instead of "Jan 1, 2024 - Jan 31, 2024"
  if (fromDate.year() === toDate.year() && fromDate.month() === toDate.month()) {
    return `${fromDate.format('MMM D')} - ${toDate.format('D, YYYY')}`
  }

  // If same year but different month, show "Jan 1 - Feb 15, 2024"
  if (fromDate.year() === toDate.year()) {
    return `${fromDate.format('MMM D')} - ${toDate.format('MMM D, YYYY')}`
  }

  // Different years, show full format
  const fromStr = fromDate.format('MMM D, YYYY')
  const toStr = toDate.format('MMM D, YYYY')

  return `${fromStr} - ${toStr}`
}

/**
 * Check if a date range object matches a preset
 * @param {Object} dateRangeObject - Object with from/to string dates
 * @param {string} presetType - One of DATE_RANGE_PRESETS values
 * @returns {boolean} True if matches preset
 */
export function isPresetDateRange(dateRangeObject, presetType) {
  const presetRange = getPresetDateRangeObject(presetType)
  return presetRange && presetRange.from === dateRangeObject.from && presetRange.to === dateRangeObject.to
}

/**
 * Find which preset matches a date range object
 * @param {Object} dateRangeObject - Object with from/to string dates
 * @returns {string|null} Preset type or null if no match
 */
export function findMatchingPreset(dateRangeObject) {
  if (!dateRangeObject) return null

  for (const preset of Object.values(DATE_RANGE_PRESETS)) {
    if (isPresetDateRange(dateRangeObject, preset)) {
      return preset
    }
  }

  return null
}

/**
 * Get preset label by value
 * @param {string} presetValue - One of DATE_RANGE_PRESETS values
 * @returns {string|null} Preset label or null if not found
 */
export function getPresetLabel(presetValue) {
  const preset = PRESET_OPTIONS.flat().find(option => option.value === presetValue)
  return preset ? preset.label : null
}
